<!--
  Market switch (live markets) for the order-value charts: one market at a time, because
  amounts are in the market's own currency and are never added together.
-->
<ng-template #marketToggle>
  <div class="market-toggle" role="group" aria-label="Chart market">
    <span class="market-toggle-label">Market</span>
    @for (m of chartMarkets; track m) {
      <label class="market-option">
        <input
          type="checkbox"
          [checked]="chartMarket() === m"
          (change)="onChartMarketChange(m, $event)"
        />
        {{ m }}
      </label>
    }
    <span class="market-currency">Amounts in {{ chartCurrency() }}</span>
  </div>
</ng-template>

<div class="main-content">
  <div class="container-fluid">
    <div class="card">
      <!-- Header -->
      <div class="header-section" style="display: flex; justify-content: space-between; align-items: center;">
        <h4 class="card-title">Merchant Orders</h4>
        <fh-refresh-button [refresh]="refreshData" />
      </div>

      <!-- Filters -->
      <fh-filter-bar (resetFilters)="onResetFilters()">
        <!-- Country Filter -->
        <fh-form-autocomplete
          [options]="countryOptions()"
          [label]="'Country'"
          [placeholder]="'Select country...'"
          [showCounts]="true"
          appearance="outline"
          [formControl]="countryFilterFormControl"
          (selectionChange)="onCountryChange($any($event))"
        />

        <!-- Merchant Filter -->
        <fh-form-autocomplete
          [options]="merchantOptions()"
          [label]="'Merchant'"
          [placeholder]="'Select merchant...'"
          [showCounts]="true"
          appearance="outline"
          [formControl]="merchantFilterFormControl"
          (selectionChange)="onMerchantChange($any($event))"
        />

        <!-- Date Range Filter -->
        <fh-date-range-filter
          [label]="'Date Range'"
          [showQuickFilters]="true"
          (dateRangeChange)="onDateRangeChange($event)"
        />
      </fh-filter-bar>

      <div class="card-body">
        <!-- Tabs -->
        <mat-tab-group (selectedIndexChange)="onTabChange($event)" [selectedIndex]="currentTab()">
          <!-- ORDERS TAB -->
          <mat-tab label="Orders">
            <div class="tab-content">
              <!-- Merchant Performance Chart -->
              @if (vendorId() && monthlyStats().length > 0) {
                <mat-card class="performance-chart">
                  <mat-card-header>
                    <mat-card-title>
                      {{ selectedVendorName() }} - 24 Month Performance ({{ chartMarket() }})
                    </mat-card-title>
                  </mat-card-header>
                  <ng-container *ngTemplateOutlet="marketToggle" />
                  <mat-card-content class="chart-content">
                    <canvas
                      baseChart
                      [type]="barChartType"
                      [data]="merchantPerformanceChartData()"
                      [options]="merchantPerformanceChartOptions()"
                    >
                    </canvas>
                  </mat-card-content>
                </mat-card>
              }

              <!-- CSV Buttons -->
              <div class="action-buttons">
                <button mat-button (click)="requestCSVEmail()">
                  Request CSV Report <mat-icon>email</mat-icon>
                </button>
                <button mat-button (click)="downloadCSV()" [disabled]="isLoading()">
                  Download CSV <mat-icon>download</mat-icon>
                </button>
              </div>

              <!-- Summary -->
              <h4 class="orders-summary">
                Orders for {{ selectedVendorName() }}
                @if (hasDateRange()) {
                  ({{ dateRange().start | date }} to {{ dateRange().end | date }})
                }
                ({{ total() }})
              </h4>

              <!-- Data Table -->
              <fh-data-table
                [config]="tableConfig()"
                [sortProp]="sortActive()"
                [sortDir]="sortDirection()"
                [pageIndex]="pageIndex()"
                [pageSize]="pageSize()"
                [actions]="tableActions()"
                (sortChange)="onSortChange($event)"
                (pageChange)="onPageChange($event)"
              />
            </div>
          </mat-tab>

          <!-- DASHBOARD TAB -->
          <mat-tab label="Dashboard">
            <div class="dashboard-container">
              <ng-container *ngTemplateOutlet="marketToggle" />
              <div class="charts-grid">
                <mat-card>
                  <mat-card-header>
                    <mat-card-title>Top Merchants by Volume</mat-card-title>
                    <mat-card-subtitle>Top 24 by confirmed order count in {{ chartMarket() }}</mat-card-subtitle>
                  </mat-card-header>
                  <mat-card-content class="chart-content">
                    <canvas
                      baseChart
                      [type]="barChartType"
                      [data]="topByVolumeData()"
                      [options]="barChartOptions"
                    >
                    </canvas>
                  </mat-card-content>
                </mat-card>

                <mat-card>
                  <mat-card-header>
                    <mat-card-title>Top Merchants by Value</mat-card-title>
                    <mat-card-subtitle>Top 24 by revenue in {{ chartMarket() }} ({{ chartCurrency() }})</mat-card-subtitle>
                  </mat-card-header>
                  <mat-card-content class="chart-content">
                    <canvas
                      baseChart
                      [type]="barChartType"
                      [data]="topByValueData()"
                      [options]="barChartOptions"
                    >
                    </canvas>
                  </mat-card-content>
                </mat-card>

                <mat-card>
                  <mat-card-header>
                    <mat-card-title>Most Products (Lifetime)</mat-card-title>
                    <mat-card-subtitle>Merchants with highest product count</mat-card-subtitle>
                  </mat-card-header>
                  <mat-card-content class="chart-content">
                    <canvas
                      baseChart
                      [type]="barChartType"
                      [data]="mostProductsData()"
                      [options]="barChartOptions"
                    >
                    </canvas>
                  </mat-card-content>
                </mat-card>

                <mat-card>
                  <mat-card-header>
                    <mat-card-title>Most Items in User Carts</mat-card-title>
                    <mat-card-subtitle>Highest cart activity</mat-card-subtitle>
                  </mat-card-header>
                  <mat-card-content class="chart-content">
                    <canvas
                      baseChart
                      [type]="barChartType"
                      [data]="mostCartItemsData()"
                      [options]="barChartOptions"
                    >
                    </canvas>
                  </mat-card-content>
                </mat-card>
              </div>
            </div>
          </mat-tab>
        </mat-tab-group>
      </div>
    </div>
  </div>
</div>
